import { Box } from "@rebass/grid";
import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import { EEnvironment } from "@rentiohq/shared-frontend/dist/config/app.config.types";
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { useBrokerFeature } from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import {
  CUSTOM_EXPORT_TYPES,
  EExportStatus,
  EExportType,
  IExportFile,
  JACK_EXPORT_BROKER_IDS,
  JACK_EXPORT_TYPES,
  exportFileModule,
} from "@rentiohq/shared-frontend/dist/reduxV2/exportFile";
import {
  MAX_SCHEDULED_EXPORTS_PER_ACCOUNT,
  scheduledExportModule,
} from "@rentiohq/shared-frontend/dist/reduxV2/scheduledExport";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { formatDateAndTime } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  DisplayText,
  EmptyState,
  Error,
  Loading,
  Pagination,
  ResourceList,
  RentioInternalRenderer,
  ResourceListItem,
  Stack,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import { useInternalMode } from "@rentiohq/web-shared/dist/redux/system/system.hooks";
import { wasScheduleSubmitted } from "forms/createCustomExport/schema.createCustomExport.actions";
import React from "react";
import { NumberParam, withDefault } from "serialize-query-params";
import { CreateCustomExportModal } from "./components/CreateCustomExportModal";
import { renderJoined } from "./components/renderJoined";
import { ScheduledExportsModal } from "./components/ScheduledExportsModal";

// The Jack reports are Jack's own (and the Rentio Test Broker's); on the test environments every
// office sees them.
const getCustomExportTypes = (brokerId?: number) =>
  CUSTOM_EXPORT_TYPES.filter(
    exportType =>
      !JACK_EXPORT_TYPES.includes(exportType) ||
      CONFIG.ENVIRONMENT !== EEnvironment.Prod ||
      (brokerId !== undefined && JACK_EXPORT_BROKER_IDS.includes(brokerId)),
  );

// A failed custom export can be tried again from its actions, so its status says that instead of
// "contact Rentio", which stays for the other exports.
const getStatusKey = (exportFile: IExportFile) =>
  exportFile.status === EExportStatus.Failed &&
  CUSTOM_EXPORT_TYPES.includes(exportFile.exportType)
    ? "export_file.status.failed_custom"
    : `export_file.status.${exportFile.status}`.toLowerCase();

export const ExportFiles = () => {
  const [queryParams, setQueryParamValue] = useQueryParams({
    page: withDefault(NumberParam, 1),
  });

  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [isCreatingRecurring, setIsCreatingRecurring] = React.useState(false);
  const [showScheduledExportsModal, setShowScheduledExportsModal] =
    React.useState(false);

  const { broker } = authHooks.useSelf();
  const hasCustomExports = useBrokerFeature(EBrokerFeature.CustomExports);
  const { internalModeEnabled } = useInternalMode();
  // Rentio staff always see the custom exports, and a note when the office lacks the feature, so
  // they never point an office at buttons it does not have. Without the feature the API still
  // makes a monitor's one-off export, but refuses a schedule.
  const showCustomExports = hasCustomExports || internalModeEnabled;
  const customExportTypes = getCustomExportTypes(broker?.id);

  const { items, isFetching, fetchError, totalPages, refetch } =
    exportFileModule.hooks.usePaged({
      shouldRefetch: true,
      query: {
        page: queryParams.page,
        limit: CONFIG.DEFAULT_FETCH_LIMIT,
        sort: [{ field: "createdAt", method: "DESC" }],
        filter: {
          exportType: { neq: EExportType.BeneficiaryReport },
        },
      },
    });

  const {
    items: scheduledExports,
    totalItems: scheduledExportsCount,
    refetch: refetchScheduledExports,
  } = scheduledExportModule.hooks.usePaged({
    shouldRefetch: true,
    query: showCustomExports
      ? {
          page: 1,
          // All of them in one page: the API keeps at most this many per office.
          limit: MAX_SCHEDULED_EXPORTS_PER_ACCOUNT,
          sort: [{ field: "createdAt", method: "DESC" }],
        }
      : undefined,
  });

  // Only once the count has loaded: a list that is loading or failed says nothing about the limit.
  const canSchedule =
    scheduledExportsCount !== undefined &&
    scheduledExportsCount < MAX_SCHEDULED_EXPORTS_PER_ACCOUNT;

  // Event handlers
  const generateHandlePressDownload =
    (exportFile: IExportFile, file: string) => async () => {
      try {
        const { data } = await exportFileModule.api.getFileUrl({
          id: exportFile.id,
          file,
        });

        const fileUrl = data.data;

        window.open(fileUrl, "_blank");
      } catch (error) {
        showAlert({
          type: "error",
          error,
        });
      }
    };

  // One retry per export at a time: a second click while the first is on its way is ignored.
  const retryingIds = React.useRef(new Set<string>());
  const generateHandlePressRetry = (exportFile: IExportFile) => async () => {
    if (retryingIds.current.has(exportFile.id)) {
      return;
    }
    retryingIds.current.add(exportFile.id);
    try {
      await exportFileModule.api.retry({ id: exportFile.id });
      showAlert({
        type: "success",
        message: getLocalizedText("export_file.retry.success"),
      });
      // The list is a paged store: reloading it is what shows the row as queued again.
      refetch();
    } catch (error) {
      showAlert({ type: "error", error });
    } finally {
      retryingIds.current.delete(exportFile.id);
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    refetch();
    refetchScheduledExports();
    // Back to the list the recurring export was added from, when one was added.
    if (isCreatingRecurring && wasScheduleSubmitted()) {
      setShowScheduledExportsModal(true);
    }
  };

  const getAppearanceVariationForStatus = (status: EExportStatus) => {
    switch (status) {
      case EExportStatus.Queued:
      case EExportStatus.New:
        return "warn";

      case EExportStatus.Failed:
        return "negative";

      case EExportStatus.Generated:
        return "positive";

      default:
        return "default";
    }
  };

  // Render
  const renderItem = (item: IExportFile) => {
    return (
      <ResourceListItem
        actions={[
          ...(item.files || []).map(file => ({
            content: getLocalizedText("system.download.filename", {
              value: `${file}.${item.exportFileType.toLowerCase()}`,
            }),
            onClick: generateHandlePressDownload(item, file),
          })),
          ...(item.status === EExportStatus.Failed &&
          CUSTOM_EXPORT_TYPES.includes(item.exportType)
            ? [
                {
                  content: getLocalizedText("export_file.retry.cta"),
                  onClick: generateHandlePressRetry(item),
                },
              ]
            : []),
        ]}
      >
        <DisplayText size="small" space="extraTight">
          {getLocalizedText(
            `export_file.export_type.${item.exportType}`.toLowerCase(),
          )}
        </DisplayText>

        <TextStyle variation="subdued">
          {renderJoined([
            <TextStyle variation={getAppearanceVariationForStatus(item.status)}>
              {getLocalizedText(getStatusKey(item))}
            </TextStyle>,
            formatDateAndTime(item.createdAt),
            item.exportFileType,
          ])}
        </TextStyle>
      </ResourceListItem>
    );
  };

  return (
    <Box>
      {showCustomExports && (
        <Box mb={24}>
          {!hasCustomExports && (
            <RentioInternalRenderer>
              This office does not have the CUSTOM_EXPORTS feature: it does not
              see these buttons. You can still make a one-off export for it, but
              no recurring export.
            </RentioInternalRenderer>
          )}
          <Stack alignment="center" distribution="trailing">
            <Button
              appearance="outline"
              onClick={() => setShowScheduledExportsModal(true)}
            >
              {getLocalizedText("scheduled_export.list.cta", {
                count: `${scheduledExportsCount || 0}`,
              })}
            </Button>
            <Button
              appearance="primary"
              onClick={() => {
                setIsCreatingRecurring(false);
                setShowCreateModal(true);
              }}
            >
              {getLocalizedText("export_file.create.cta")}
            </Button>
          </Stack>
        </Box>
      )}

      {!items && isFetching && <Loading />}

      {!items && fetchError && <Error errors={[fetchError]} />}

      {items?.length === 0 && (
        <EmptyState
          icon="archive"
          heading={getLocalizedText("export_files.empty.title")}
          action={{
            content: getLocalizedText("system.reload"),
            onClick: () => {
              refetch();
            },
          }}
        />
      )}

      <ResourceList items={items || []} renderItem={renderItem} />

      {!!totalPages && totalPages > 1 && (
        <Pagination
          initialPage={queryParams.page - 1}
          pageCount={totalPages}
          onPageChange={({ selected }) => {
            setQueryParamValue({ page: selected + 1 });
          }}
        />
      )}

      {showScheduledExportsModal && (
        <ScheduledExportsModal
          scheduledExports={scheduledExports || []}
          onChange={() => {
            refetch();
            refetchScheduledExports();
          }}
          canCreate={canSchedule}
          onCreate={() => {
            setShowScheduledExportsModal(false);
            setIsCreatingRecurring(true);
            setShowCreateModal(true);
          }}
          onClose={() => setShowScheduledExportsModal(false)}
        />
      )}

      {showCreateModal && (
        <CreateCustomExportModal
          exportTypes={customExportTypes}
          isRecurring={isCreatingRecurring}
          canSchedule={canSchedule}
          onClose={() => {
            setShowCreateModal(false);
            // Opened from the recurring exports list: closing goes back to it.
            if (isCreatingRecurring) {
              setShowScheduledExportsModal(true);
            }
          }}
          onSuccess={handleCreateSuccess}
        />
      )}
    </Box>
  );
};
